export type PinPoint = {
  x: number
  y: number
}

export type IdentifiedPin = PinPoint & {
  id: string
}

export type RectLike = {
  left: number
  top: number
  width: number
  height: number
}

export type ClientPointLike = {
  clientX: number
  clientY: number
}

export type PinPositionStyle = {
  left: string
  top: string
  transform: string
}

export const DEFAULT_PIN_POSITIONS: readonly PinPoint[] = [
  { x: 27, y: 30 },
  { x: 68, y: 24 },
  { x: 40, y: 64 },
  { x: 73, y: 70 },
]

export const clampPercent = (value: number, fallback = 0): number => {
  if (!Number.isFinite(value)) return fallback
  return Math.min(100, Math.max(0, value))
}

export const normalizePin = <T extends PinPoint>(pin: T): T => ({
  ...pin,
  x: clampPercent(pin.x),
  y: clampPercent(pin.y),
})

export const pointFromClient = (point: ClientPointLike, rect: RectLike): PinPoint => {
  if (rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 }

  return normalizePin({
    x: ((point.clientX - rect.left) / rect.width) * 100,
    y: ((point.clientY - rect.top) / rect.height) * 100,
  })
}

export const percentToPixel = (pin: PinPoint, rect: Pick<RectLike, "width" | "height">): PinPoint => ({
  x: (clampPercent(pin.x) / 100) * rect.width,
  y: (clampPercent(pin.y) / 100) * rect.height,
})

export const pinToPositionStyle = (pin: PinPoint): PinPositionStyle => {
  const normalized = normalizePin(pin)
  return {
    left: `${normalized.x}%`,
    top: `${normalized.y}%`,
    transform: "translate(-50%, -50%)",
  }
}

export const pinToCssVars = (pin: PinPoint, prefix = "--pin"): Record<string, string> => {
  const normalized = normalizePin(pin)
  return {
    [`${prefix}-x`]: `${normalized.x}%`,
    [`${prefix}-y`]: `${normalized.y}%`,
  }
}

export const distancePercent = (a: PinPoint, b: PinPoint): number => {
  const dx = clampPercent(a.x) - clampPercent(b.x)
  const dy = clampPercent(a.y) - clampPercent(b.y)
  return Math.hypot(dx, dy)
}

export const arePinsNear = (a: PinPoint, b: PinPoint, thresholdPercent = 4): boolean =>
  distancePercent(a, b) <= thresholdPercent

export const distributePins = (count: number, positions: readonly PinPoint[] = DEFAULT_PIN_POSITIONS): PinPoint[] => {
  if (count <= 0) return []
  if (positions.length === 0) return Array.from({ length: count }, () => ({ x: 50, y: 50 }))

  return Array.from({ length: count }, (_, index) => normalizePin(positions[index % positions.length] ?? { x: 50, y: 50 }))
}

export const sortPinsTopToBottom = <T extends PinPoint>(pins: readonly T[]): T[] =>
  [...pins].sort((a, b) => a.y - b.y || a.x - b.x)
